import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck, Heart, Star, CheckCircle2, ArrowRight } from 'lucide-react';
import { optimizeImage, generateSrcSet } from '../utils';
import SEO from '../components/SEO';
import Schema from '../components/Schema';

const AboutPage = () => {
  const navigate = useNavigate();

  return (
    <div className='min-h-screen pt-[72px] bg-white animate-fade-in'>
      <SEO
        title="About Our Family Business"
        description="Discover the history of Mainland Fireplaces. A family-owned business bringing warmth and comfort to homes across the Fraser Valley with over 25 years of expertise."
        keywords="about mainland fireplaces, fireplace experts langley, fireplace history, family business langley"
      />
      <Schema
        type="BreadcrumbList"
        data={{
          items: [
            { name: 'Home', path: '/' },
            { name: 'About', path: '/about' }
          ]
        }}
      />
      {/* HEADER SECTION */}
      <section className='relative w-full h-[60vh] flex items-center justify-center bg-secondary overflow-hidden'>
        <div className='absolute inset-0'>
          <img
            src={optimizeImage(`${import.meta.env.BASE_URL}images/headers/about.avif`, 1200)}
            srcSet={generateSrcSet(`${import.meta.env.BASE_URL}images/headers/about.avif`)}
            sizes="100vw"
            alt='About Mainland Fireplaces — premium fireplace and outdoor living experts since 2001'
            width={1200}
            height={800}
            fetchPriority="high"
            className='w-full h-full object-cover opacity-40'
          />
          <div className='absolute inset-0 bg-linear-to-t from-secondary via-secondary/70 to-transparent'></div>
        </div>

        <div className='relative z-10 text-center max-w-4xl px-6'>
          <div className='flex items-center justify-center gap-4 mb-4'>
            <span className='h-px w-12 bg-primary'></span>
            <span className='text-primary font-bold tracking-[0.25em] uppercase text-xs animate-in slide-in-from-bottom duration-700'>
              Since 2001
            </span>
            <span className='h-px w-12 bg-primary'></span>
          </div>
          <h1 className='font-heading text-5xl md:text-7xl text-white uppercase leading-none mb-6 drop-shadow-lg animate-in fade-in zoom-in duration-1000'>
            Keeping Families <br />
            <span className='text-primary'>Cozy</span>
          </h1>
          <p className='font-sans text-gray-300 text-lg md:text-xl max-w-2xl mx-auto leading-relaxed animate-in slide-in-from-bottom duration-1000 delay-200'>
            We believe your home should be a place of warmth, comfort, and connection.
            Enhancing living spaces inside and out for over two decades.
          </p>
        </div>
      </section>

      {/* ABOUT SECTION */}
      <section className='py-24 px-6 md:px-12 lg:px-24 max-w-7xl mx-auto'>
        <div className='flex flex-col lg:flex-row items-center gap-16'>
          <div className='flex-1 relative w-full'>
            <div className='relative aspect-4/5 w-full max-w-md mx-auto lg:mr-auto'>
              <img
                src={optimizeImage(`${import.meta.env.BASE_URL}images/headers/about_us.avif`, 800)}
                srcSet={generateSrcSet(`${import.meta.env.BASE_URL}images/headers/about_us.avif`)}
                sizes="(max-width: 1024px) 100vw, 800px"
                alt="Professional luxury fireplace installation by Mainland Fireplaces team"
                width={800}
                height={1000}
                className='w-full h-full object-cover rounded-sm shadow-2xl'
              />

              <div className='absolute -bottom-4 md:-bottom-10 -right-4 md:-right-10 bg-white rounded-sm p-6 shadow-xl border-l-4 border-primary block animate-in fade-in slide-in-from-right duration-1000 delay-300'>
                <div className='flex flex-col items-center'>
                  <span className='font-heading text-5xl text-primary font-bold'>25+</span>
                  <span className='text-xs uppercase tracking-widest text-secondary font-bold'>Years Experience</span>
                </div>
              </div>
            </div>
          </div>

          <div className='flex-1 space-y-8'>
            <h2 className='font-heading text-4xl md:text-5xl text-secondary uppercase'>
              Who We <span className='text-primary'>Are</span>
            </h2>
            <div className='space-y-6 text-gray-700 font-sans leading-relaxed text-lg'>
              <p>
                Founded with a simple mission in 2001, <span className='font-bold text-secondary'>Mainland Fireplaces</span> has grown from a local shop to a trusted leader in Langley and beyond. We don't just sell fireplaces; we curate the heart of your home.
              </p>
              <p>
                With over 25 years of industry experience, our team is known for <span className='text-primary font-bold'>honest advice, high-quality installations, and personal service</span>. Whether it's a sleek electric unit for a modern condo or a custom outdoor kitchen for your dream backyard, we guide you through every step with care.
              </p>
            </div>

            <div className='flex flex-col gap-4 pt-4'>
              {[
                "Locally Owned & Community Driven",
                "Expert In-House Installation Teams",
                "Premium Partners (Napoleon, Dimplex, Bromic)"
              ].map((item, i) => (
                <div key={i} className='flex items-center gap-3'>
                  <div className='w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center text-primary'>
                    <CheckCircle2 className='w-4 h-4' />
                  </div>
                  <span className='font-sans font-bold text-secondary text-sm uppercase tracking-wide'>{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CORE VALUES SECTION */}
      <section className='bg-gray-50 py-24 px-6 border-y border-gray-100'>
        <div className='max-w-7xl mx-auto'>
          <div className='text-center mb-16'>
            <div className='flex items-center justify-center gap-4 mb-4'>
              <span className='h-px w-12 bg-primary'></span>
              <span className='text-primary font-bold tracking-[0.2em] uppercase text-xs animate-in slide-in-from-bottom duration-700'>
                Why Choose Us
              </span>
              <span className='h-px w-12 bg-primary'></span>
            </div>
            <h2 className='font-heading text-4xl md:text-5xl text-secondary uppercase mt-4'>
              The Mainland <span className='text-primary'>Difference</span>
            </h2>
          </div>

          <div className='grid grid-cols-1 md:grid-cols-3 gap-8'>
            {[
              {
                icon: ShieldCheck,
                title: "Trusted Craftsmanship",
                desc: "With over two decades of hands-on experience, we ensure every installation is done with precision, safety, and care."
              },
              {
                icon: Star,
                title: "Premium Products",
                desc: "We strictly partner with top-tier brands like Napoleon® and Dimplex. Built to last, designed to impress, and warrantied for peace of mind."
              },
              {
                icon: Heart,
                title: "Community First",
                desc: "As a local business, we thrive on word-of-mouth. Over 95% of our clients recommend us to friends and family."
              }
            ].map((feature, i) => (
              <div key={i} className='group bg-white p-10 shadow-sm rounded-sm border border-gray-200 hover:border-primary hover:shadow-xl transition-all duration-300 hover:-translate-y-2'>
                <div className='w-14 h-14 bg-primary text-white flex items-center justify-center mb-6 group-hover:bg-secondary transition-colors duration-300'>
                  <feature.icon className='w-7 h-7' />
                </div>
                <h3 className='font-heading text-2xl text-secondary uppercase mb-4'>{feature.title}</h3>
                <p className='font-sans text-gray-700 leading-relaxed'>
                  {feature.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* STATS BANNER SECTION */}
      <section className='bg-primary text-white py-16 px-6'>
        <div className='max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-12 text-center md:text-left'>
          <div className='flex flex-col gap-2'>
            <h2 className='font-heading text-4xl uppercase'>Real Results</h2>
            <p className='text-white font-sans max-w-md'>We take pride in the numbers that define our journey serving Langley since 2001.</p>
          </div>

          <div className='flex gap-12 md:gap-24'>
            <div className='flex flex-col items-center'>
              <span className='font-heading text-5xl md:text-6xl font-bold'>25+</span>
              <span className='text-xs uppercase tracking-widest font-bold mt-2 text-white/90'>Years</span>
            </div>
            <div className='flex flex-col items-center'>
              <span className='font-heading text-5xl md:text-6xl font-bold'>1k+</span>
              <span className='text-xs uppercase tracking-widest font-bold mt-2 text-white/90'>Homes Warmed</span>
            </div>
            <div className='flex flex-col items-center'>
              <span className='font-heading text-5xl md:text-6xl font-bold'>95%</span>
              <span className='text-xs uppercase tracking-widest font-bold mt-2 text-white/90'>Satisfaction</span>
            </div>
          </div>
        </div>
      </section>

      {/* CTA SECTION */}
      <section className='relative py-32 px-6 flex items-center justify-center text-center overflow-hidden bg-white'>
        <div className='max-w-3xl relative z-10'>
          <h2 className='font-heading text-4xl md:text-6xl text-secondary uppercase mb-6'>
            Ready to Transform <br /> Your <span className='text-primary'>Space?</span>
          </h2>
          <p className='font-sans text-gray-700 text-lg mb-10 max-w-xl mx-auto'>
            Visit our showroom or book a consultation to discuss your project.
            We are here to bring the warmth inside and out.
          </p>
          <div className='flex flex-col sm:flex-row gap-4 justify-center'>
            <button
              onClick={() => navigate('/products')}
              className='px-8 py-4 bg-primary border-2 border-primary text-white uppercase font-bold rounded-sm tracking-widest text-xs hover:bg-secondary hover:border-secondary transition-all shadow-lg flex items-center justify-center gap-2 group'
            >
              Browse Collection <ArrowRight className='w-4 h-4 group-hover:translate-x-1 transition-transform' />
            </button>
            <button
              onClick={() => navigate('/contact')}
              className='px-8 py-4 border-2 border-primary text-primary uppercase font-bold rounded-sm tracking-widest text-xs hover:bg-secondary hover:border-secondary hover:text-white transition-all shadow-lg flex items-center justify-center gap-2 group'
            >
              Book Consultation <ArrowRight className='w-4 h-4 group-hover:translate-x-1 transition-transform' />
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;